import { useMemo } from 'react'
import { unique } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import { excludeNullOrUndefined } from 'checkout/utils'

export default function useSearchMapResults(
  selectedOfferIds: Array<string>,
  overrideOfferList?: App.OfferList,
  pageSize: number = 20,
  offerList?: App.OfferList,
) {
  const offerIds = useMemo(() => {
    if (!offerList?.fetching && overrideOfferList) {
      return unique([...overrideOfferList.offerIds, ...selectedOfferIds])
    }
    return []
  }, [offerList?.fetching, overrideOfferList, selectedOfferIds])

  const [loadedOffers, offersFetching] = useOffers<App.HotelOffer | App.BedbankOffer>(offerIds)

  const offers = useMemo(() => {
    const loadedOfferMap = new Map(loadedOffers.map(offer => [offer.id, offer]))
    return offerIds.map(id => loadedOfferMap.get(id)).filter(excludeNullOrUndefined)
  }, [loadedOffers, offerIds])

  const totalNumOffers = overrideOfferList?.offerCount || 0

  const fetchingList = (offerList?.offerIds?.length || 0) < totalNumOffers
  // Only treat the current page as "still loading" while the offer list is actively
  // fetching. Otherwise a partial page (e.g. 15/20) with a large unfinished stream
  // keeps the "Loading offers" banner stuck even though markers are already shown.
  const fetchingPage = (overrideOfferList?.offerIds?.length || 0) < pageSize &&
    fetchingList &&
    !overrideOfferList?.streamEnded &&
    !!offerList?.fetching
  const error = overrideOfferList?.error && !overrideOfferList?.streamEnded
  const allOffersLoaded = !fetchingList && !offersFetching && offers.length === totalNumOffers
  const noOffersAvailable = !fetchingList && totalNumOffers === 0
  const key = overrideOfferList?.key

  return useMemo(() => ({
    offers,
    totalNumOffers,
    fetchingList,
    error,
    allOffersLoaded,
    noOffersAvailable,
    key,
    fetchingPage,
  }), [offers, totalNumOffers, fetchingList, error, allOffersLoaded, noOffersAvailable, key, fetchingPage])
}
